import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FcGoogle } from "react-icons/fc";
import { FaApple } from "react-icons/fa";
import { ReactSVG } from "react-svg";
import microsoftIcon from "../../Asset/microsoft icon.svg";
import zeroTypingLogo from "../../Asset/ZEROTyinglogoLogin.png";

const LoginNew = () => {
  const [searchParams] = useSearchParams();
  const error = searchParams.get("error");
  const navigate = useNavigate();

  const handleMicrosoftLogin = () => {
    const clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
    const tenant = "common";
    const redirectUri = `${process.env.REACT_APP_BACKEND_URL}api/auth/microsoft`;

    const authUrl = `https://login.microsoftonline.com/${tenant}/oauth2/v2.0/authorize?${new URLSearchParams(
      {
        client_id: clientId,
        response_type: "code",
        redirect_uri: redirectUri,
        response_mode: "form_post",
        scope: "openid email profile User.Read",
        prompt: "select_account",
      }
    )}`;

    window.location.href = authUrl;
  };

  const handleAppleLogin = () => {
    const clientId = process.env.REACT_APP_APPLE_CLIENT_ID;
    const redirectUri = `${process.env.REACT_APP_BACKEND_URL}api/auth/apple`;

    const state = Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    sessionStorage.setItem("apple_oauth_state", state);

    const authUrl = `https://appleid.apple.com/auth/authorize?${new URLSearchParams(
      {
        response_type: "code",
        response_mode: "form_post",
        client_id: clientId,
        redirect_uri: redirectUri,
        scope: "name email",
        state,
      }
    )}`;

    window.location.href = authUrl;
  };

  const handleGoogleLogin = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = `${process.env.REACT_APP_BACKEND_URL}api/auth/google`;

    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${new URLSearchParams(
      {
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: "code",
        scope: "openid email profile",
        access_type: "offline",
        prompt: "select_account",
      }
    )}`;

    window.location.href = authUrl;
  };

  useEffect(() => {
    if (error) {
      switch (error) {
        case "missing_code":
          toast.error("We couldn’t complete the login. Please try again.");
          break;
        case "user_not_registered":
          toast.error(
            "Your account was not found, please sign up using our application"
          );
          navigate("/download");
          break;
        case "auth_failed":
          toast.error(
            "Login failed. Please check your credentials and try again."
          );
          break;
        case "service_unavailable":
          toast.error(
            "We're experiencing some technical issues and our team is working to fix it. Please try again later."
          );
          break;
        default:
          toast.error("Oops! Something went wrong. Please try again later.");
      }
    }
  }, [error, navigate]);

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  return (
    <div className="h-screen overflow-hidden mx-auto flex flex-col">
      <div className="relative flex-1 overflow-hidden bg-white flex flex-col">
        {/* Floating orbit background — same as Home */}
        <div className="absolute top-0 left-0 w-full h-full flex flex-col z-0">
          <div className="relative flex-[1] bg-[#FFFFFF]">
            <svg
              className="absolute top-0 left-0 w-full h-full z-[4]"
              viewBox="0 0 1000 1350"
              preserveAspectRatio="none"
            >
              <path
                id="lgnOrbitPath"
                d="M40,0 H960 A40,40 0 0 1 1000,40 V1310 A40,40 0 0 1 960,1350 H40 A40,40 0 0 1 0,1310 V40 A40,40 0 0 1 40,0 Z"
                fill="none"
                stroke="transparent"
              />
              <circle r="300" fill="url(#lgnGlow)" filter="url(#lgnBlur)">
                <animateMotion
                  repeatCount="indefinite"
                  dur="20s"
                  rotate="auto"
                >
                  <mpath href="#lgnOrbitPath" />
                </animateMotion>
              </circle>
              <circle r="300" fill="url(#lgnGlow)" filter="url(#lgnBlur)">
                <animateMotion
                  repeatCount="indefinite"
                  dur="20s"
                  rotate="auto"
                  keyPoints="1;0"
                  keyTimes="0;1"
                >
                  <mpath href="#lgnOrbitPath" />
                </animateMotion>
              </circle>
              <defs>
                <radialGradient id="lgnGlow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#3AB1F6" stopOpacity=".4" />
                  <stop offset="100%" stopColor="#3AB1F6" stopOpacity="0" />
                </radialGradient>
              </defs>
              <filter
                id="lgnBlur"
                x="-100%"
                y="-100%"
                width="300%"
                height="300%"
              >
                <feGaussianBlur in="SourceGraphic" stdDeviation="20" />
              </filter>
            </svg>
          </div>
        </div>

        {/* Back button */}
        <div className="relative z-10 p-5 sm:p-6">
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-2 bg-[#F3F4F6] hover:bg-[#E5E7EB] transition-colors rounded-full px-4 py-2 font-inter font-medium text-[#0F1B2D] text-sm shadow-sm"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back
          </button>
        </div>

        {/* Centered content */}
        <div className="relative z-10 flex-1 min-h-0 flex flex-col items-center justify-center px-4 py-2">
          <img
            src={zeroTypingLogo}
            alt="Zero Typing"
            className="w-[160px] sm:w-[190px] md:w-[211px] h-auto"
          />

          <div
            className="w-full max-w-[675px] flex flex-col items-center"
            style={{ paddingLeft: 27, paddingRight: 27, gap: 27, marginTop: 40 }}
          >
            {/* Heading + Text */}
            <div
              className="w-full max-w-[621px] flex flex-col items-center"
              style={{ gap: 8 }}
            >
              <h1
                className="text-center text-[#111111] font-semibold text-[24px] sm:text-[28px] md:text-[32px] leading-[1.25] md:leading-[40px]"
                style={{
                  fontFamily:
                    "'SF Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  fontWeight: 590,
                }}
              >
                The Quickest Way from Thought to Text.
              </h1>
              <p
                className="text-center text-[#4B4B4B] text-[14px] sm:text-[15px] md:text-[16px] leading-[24px] max-w-[571px] font-inter font-normal"
              >
                Speak naturally and transform thoughts into text instantly.
              </p>
            </div>

            {/* Buttons */}
            <div
              className="w-full max-w-[537px] flex flex-col"
              style={{ gap: 16 }}
            >
              <button
                type="button"
                onClick={handleGoogleLogin}
                className="w-full h-[56px] rounded-[12px] bg-white hover:shadow-md transition-shadow flex items-center justify-center text-[#111111]"
                style={{
                  border: "1px solid #0000001A",
                  padding: 16,
                  gap: 8,
                  fontFamily:
                    "'SF Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  fontWeight: 590,
                  fontSize: 16,
                  lineHeight: "24px",
                  letterSpacing: "-0.04em",
                }}
              >
                <FcGoogle className="size-5 shrink-0" />
                Continue With Google
              </button>

              <button
                type="button"
                onClick={handleMicrosoftLogin}
                className="w-full h-[56px] rounded-[12px] bg-white hover:shadow-md transition-shadow flex items-center justify-center text-[#111111]"
                style={{
                  border: "1px solid #0000001A",
                  padding: 16,
                  gap: 8,
                  fontFamily:
                    "'SF Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  fontWeight: 590,
                  fontSize: 16,
                  lineHeight: "24px",
                  letterSpacing: "-0.04em",
                }}
              >
                <ReactSVG
                  src={microsoftIcon}
                  beforeInjection={(svg) => {
                    svg.setAttribute("class", "w-5 h-5 shrink-0");
                  }}
                />
                Continue With Microsoft
              </button>

              <button
                type="button"
                onClick={handleAppleLogin}
                className="w-full h-[56px] rounded-[12px] bg-white hover:shadow-md transition-shadow flex items-center justify-center text-[#111111]"
                style={{
                  border: "1px solid #0000001A",
                  padding: 16,
                  gap: 8,
                  fontFamily:
                    "'SF Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  fontWeight: 590,
                  fontSize: 16,
                  lineHeight: "24px",
                  letterSpacing: "-0.04em",
                }}
              >
                <FaApple className="size-5 shrink-0" />
                Sign in with Apple
              </button>
            </div>
          </div>
        </div>

        {/* Footer disclaimer */}
        <div className="relative z-10 px-4 pb-8 sm:pb-10 pt-10">
          <p className="font-inter text-[#4B5563] text-center text-[0.8125rem] sm:text-[0.875rem] leading-[1.5]">
            By using Zero Typing AI you agree to the
            <br />
            <a
              href="/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#3AB1F6] font-semibold hover:underline"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#3AB1F6] font-semibold hover:underline"
            >
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginNew;
